import { inject, Injectable } from '@angular/core';
import { NotificationDtoJson } from '@shared/models/api/models/NotificationDtoJson';
import { BehaviorSubject } from 'rxjs';
import { WebSocketService } from './websocket.service';
import { AuthService } from '@features/auth/services/auth.service';
import { NOTIFICATION_STORAGE_KEY } from '@shared/constants/core/notification';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly wsService = inject(WebSocketService);
  private readonly authService = inject(AuthService);

  // Liste des notifications
  private readonly notificationsSubject = new BehaviorSubject<NotificationDtoJson[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  // Nouvelle notification
  private readonly newNotificationSubject = new BehaviorSubject<NotificationDtoJson | null>(null);
  public newNotification$ = this.newNotificationSubject.asObservable();

  // Compteur de notifications non lues
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  // Subscription Ids
  private personalSubscriptionId?: string;
  private broadcastSubscriptionId?: string;

  constructor() {
    // charger les notifications depuis le stockage local
    this.loadNotificationsFromStorage();

    // abonner aux notifications via websocket
    this.wsService.connected$.subscribe(() => {
      this.subscribeToNotification();
    });

    // se déconnecter proprement lors du logout
    // TODO: écouter un event de logout global
  }

  /**
   * Initialise le service de notification.
   * @param userId L'ID de l'utilisateur.
   * @param token Le token d'authentification.
   */
  init(userId: string, token?: string): void {
    console.log(`NotificationService initializing for user ${userId}...`);

    // se connecter au websocket
    this.wsService.connect(userId, token);
  }

  /**
   * S'abonne aux notifications.
   * @returns void
   */
  private subscribeToNotification(): void {
    const userId = this.authService.currentUser()?.reference;
    if (!userId) {
      console.error('Cannot subscribe to notifications: user ID is missing.');
      return;
    }

    console.log(`Subscribing to notifications for user: ${userId}`);

    // s'abonner aux notifications personnelles
    // IMPORTANT: On utilise /user/queue/notifications (sans userId)
    // STOMP va automatiquement le mapper à /user/{sessionId}/queue/notifications
    const personalChannel = `/user/queue/notifications`;
    console.log(`📨 Personal channel: ${personalChannel}`);
    this.personalSubscriptionId = this.wsService.subscribe<NotificationDtoJson>(
      personalChannel,
      (message) => this.handleNewNotifications(message)
    );
    console.log(`Subscribed to personal notifications (ID: ${this.personalSubscriptionId})`);

    // s'abonner aux notifications broadcast
    const broadcastChannel = `/topic/notifications/broadcast`;
    console.log(`Broadcast channel: ${broadcastChannel}`);
    this.broadcastSubscriptionId = this.wsService.subscribe<NotificationDtoJson>(
      broadcastChannel,
      (message) => this.handleNewNotifications(message)
    );
    console.log(`Subscribed to broadcast notifications (ID: ${this.broadcastSubscriptionId})`);
  }

  /**
   * Gère les nouvelles notifications.
   * @param notification La notification à traiter.
   */
  private handleNewNotifications(notification: NotificationDtoJson): void {
    console.log('New notification received:', notification);

    // ajouter la notification au début de la liste
    const notifications = [notification, ...this.notificationsSubject.value];
    this.notificationsSubject.next(notifications);

    // sauvegarder dans le stockage local
    this.saveNotificationsToStorage(notifications);

    // mettre à jour le compteur de non lus
    this.updateUnreadCount();

    // émettre la nouvelle notification
    this.newNotificationSubject.next(notification);
  }

  /**
   * Marque une notification comme lue.
   * @param notificationId L'ID de la notification à marquer comme lue.
   */
  markAsRead(notificationId: number): void {
    console.log(`Marking notification ${notificationId} as read...`);

    // mettre à jour la notification dans la liste
    const notifications = this.notificationsSubject.value.map((n) =>
      n.alertId === notificationId ? { ...n, read: true } : n
    );

    this.notificationsSubject.next(notifications);
    this.saveNotificationsToStorage(notifications);
    this.updateUnreadCount();

    // envoyer au serveur via websocket
    try {
      this.wsService.send(`/app/notifications/mark-read`, { alertId: notificationId });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Supprime une notification.
   * @param notificationId L'ID de la notification à supprimer.
   */
  removeNotification(notificationId: number): void {
    const notifications = this.notificationsSubject.value.filter(
      (n) => n.alertId !== notificationId
    );
    this.notificationsSubject.next(notifications);
    this.saveNotificationsToStorage(notifications);
    this.updateUnreadCount();
  }

  /**
   * Supprime toutes les notifications.
   */
  clearAll(): void {
    this.notificationsSubject.next([]);
    this.saveNotificationsToStorage([]);
    this.updateUnreadCount();
  }

  /**
   * Récupère la liste des notifications.
   * @returns La liste des notifications.
   */
  getNotifications(): NotificationDtoJson[] {
    return this.notificationsSubject.value;
  }

  /**
   * Récupère le nombre de notifications non lues.
   * @returns Le nombre de notifications non lues.
   */
  getUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  disconnect(): void {
    // se désabonner des notifications
    if (this.personalSubscriptionId) {
      this.wsService.unsubscribe(this.personalSubscriptionId);
    }
    if (this.broadcastSubscriptionId) {
      this.wsService.unsubscribe(this.broadcastSubscriptionId);
    }

    this.wsService.disconnect();
    console.log('NotificationService disconnected.');
  }

  // ========== MÉTHODES PRIVÉES ==========
  /**
   * Met à jour le compteur de notifications non lues.
   */
  private updateUnreadCount(): void {
    const count = this.notificationsSubject.value.filter((n) => !n.read).length;
    this.unreadCountSubject.next(count);
  }

  /**
   * Charge les notifications depuis le stockage local.
   */
  private loadNotificationsFromStorage(): void {
    try {
      const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
      if (stored) {
        const notifications = JSON.parse(stored) as NotificationDtoJson[];
        this.notificationsSubject.next(notifications);
        this.updateUnreadCount();
      }
    } catch (error) {
      console.error('Error loading notifications from storage:', error);
    }
  }

  /**
   * Sauvegarde les notifications dans le stockage local.
   * @param notification La liste des notifications à sauvegarder.
   */
  private saveNotificationsToStorage(notification: NotificationDtoJson[]): void {
    try {
      // garder seulment les 100 dernières notifications
      const toStore = notification.slice(0, 100);
      localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
      console.error('Error saving notifications to storage:', error);
    }
  }
}
